import React, { useMemo, useCallback } from "react";
import {
  formatDate,
  Flex,
  Button,
  Space,
  Box,
  ActivityIndicator,
  TableCellText,
  TableCell,
} from "@thenounproject/lingo-core";

import Chart from "../Chart";
import InsightsIntervalPicker from "../InsightsIntervalPicker";
import useFetchPortalViews from "../queries/usePortalViewIntervals";
import DataTable from "../../table/DataTable";
import { TableColumn } from "../../table";
import usePortalViewsByUser, { PortalUserResult } from "../queries/usePortalViewsByUser";
import { LoadFailure } from "../ReportElements";
import useGenerateInsightsCSV from "../hooks/useGenerateInsightsCSV";

import { useInsightsContext } from "@contexts/InsightsProvider";
import { formatInsightsDate } from "../utils";
import { getSpaceRoleDisplayName } from "../../../constants/displayUserRoles";

type Props = {
  portalId: string;
  space: Space;
};

const InsightsPortalViewTab = ({ portalId, space }: Props) => {
  const [sort, setSort] = React.useState<string>("-last_viewed");

  const [generateInsightsCSV] = useGenerateInsightsCSV();

  const { dateOperators } = useInsightsContext();

  const { data, error: dataFetchingError } = useFetchPortalViews({
    spaceId: space.id,
    portalId,
    dateOperators,
  });
  const { result: portalViewData } = data || {};
  const {
    data: fetchedData,
    error: tableError,
    fetchNextPage,
    isLoading: tableIsLoading,
  } = usePortalViewsByUser({
    spaceId: space.id,
    portalId,
    sort,
    dateOperators,
  });

  const { rows: tableData, total } = fetchedData ?? {};

  const hasMorePages = tableData?.length < total;

  const { portalViewLabels, portalViewMembers, portalViewVisitors } = useMemo(
    () =>
      (portalViewData || []).reduce(
        (acc, item, i) => {
          acc.portalViewLabels[i] = formatInsightsDate(item.date);
          acc.portalViewMembers[i] = item.memberViews;
          acc.portalViewVisitors[i] = item.guestViews;
          return acc;
        },
        { portalViewLabels: [], portalViewMembers: [], portalViewVisitors: [] }
      ),
    [portalViewData]
  );

  const downloadCSV = useCallback(async () => {
    await generateInsightsCSV({
      spaceId: space.id,
      id: "user_portal_usage",
      sort,
      dateOperators,
      additionalParams: { ["portal_uuid"]: portalId },
    });
  }, [dateOperators, generateInsightsCSV, portalId, sort, space.id]);

  const tableHeaderProps = useMemo(() => {
    return {
      title: "Views by User",
      subtitle: "Which users and members viewed the portal.",
      tooltip:
        "Users include anyone joined to your space, Visitors include anyone not joined to your space.",
      headerExtra: (
        <Flex>
          <InsightsIntervalPicker id={"user-table"} />
          <Button
            icon="action.download"
            buttonStyle="secondary"
            ml="s"
            onClick={downloadCSV}
            disabled={tableData?.length === 0}
          />
        </Flex>
      ),
    };
  }, [downloadCSV, tableData?.length]);

  const tableColumns = useMemo(() => {
    const cols: TableColumn<PortalUserResult>[] = [
      {
        sortKey: "name",
        name: "Name",
        minWidth: 340,
        flexGrow: 1,
        renderer: u => {
          return <TableCell data={u} space={space} />;
        },
      },
      {
        sortKey: "role",
        name: "Role",
        minWidth: 160,
        renderer: u => <TableCellText>{getSpaceRoleDisplayName(u.role, space)}</TableCellText>,
      },
      {
        sortKey: "total_views",
        name: "Total Views",
        minWidth: 160,
        justify: "flex-end",
        renderer: u => <TableCellText>{u?.totalViews}</TableCellText>,
      },
      {
        sortKey: "last_viewed",
        name: "Last Viewed",
        minWidth: 160,
        renderer: u => <TableCellText>{formatDate(u.lastViewed, "MMM d, yyyy")}</TableCellText>,
      },
    ];
    return cols;
  }, [space]);

  if (dataFetchingError) return <LoadFailure />;
  if (!portalViewData) {
    return (
      <Box height="50vh">
        <ActivityIndicator size="large" center />
      </Box>
    );
  }

  return (
    <>
      <Box px="xl" pt="l">
        <Chart
          title="Portal Views"
          subtitle="The number of times your portals were viewed by users and visitors."
          tooltip="Users include anyone joined to your space, Visitors include anyone not joined to your space."
          error={!!dataFetchingError}
          headerExtra={<InsightsIntervalPicker id="portals-modal" />}
          labels={portalViewLabels}
          values={[
            {
              label: "Views by Users",
              data: portalViewMembers,
              color: "primaryColorDark",
            },
            {
              label: "Views by Visitors",
              data: portalViewVisitors,
              color: "primaryColorLight",
            },
          ]}
          Ylabel="# of Views"
        />
      </Box>
      <DataTable<PortalUserResult>
        data={tableData}
        columns={tableColumns}
        reportTitleProps={tableHeaderProps}
        isLoading={tableIsLoading}
        hasMorePages={hasMorePages}
        fetchNextPage={fetchNextPage}
        onSort={setSort}
        sort={sort}
        tableError={tableError}
      />
    </>
  );
};

export default InsightsPortalViewTab;
